import type { DateRange } from 'src/components/range-availability-calendar';

import { memo, useCallback, useMemo } from 'react';
import { Datepicker } from '@mobiscroll/react';

import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';

import { getActivityDateRangePickerSx } from './activity-date-range-picker-styles';
import {
  getActivityDateRangePickerBounds,
  isMobiscrollRangeWithinBounds,
  normalizeMobiscrollRangeValue,
} from './utils';

export type ActivityDateRangePickerProps = {
  /** Activity completion window — selectable days are clamped to today through window end. */
  completionWindow?: DateRange;
  /** When true, any date may be selected and month navigation is not clamped to the window. */
  unrestricted?: boolean;
  value?: [Date, Date] | null;
  onChange?: (range: [Date, Date] | null) => void;
  /** Remount key when switching edit targets or resetting selection. */
  instanceKey?: string;
  pages?: number;
  sx?: SxProps<Theme>;
};

export const ActivityDateRangePicker = memo(
  ({
    completionWindow,
    unrestricted = false,
    value = null,
    onChange,
    instanceKey = 'default',
    pages = 2,
    sx,
  }: ActivityDateRangePickerProps) => {
    const theme = useTheme();

    const pickerBounds = useMemo(() => {
      if (unrestricted) {
        return { hasSelectableRange: true as const };
      }

      return getActivityDateRangePickerBounds(completionWindow!);
    }, [completionWindow, unrestricted]);

    const pickerInvalidRanges = useMemo(() => {
      if (unrestricted) {
        return undefined;
      }

      if (!('from' in pickerBounds) || !pickerBounds.hasSelectableRange) {
        return [{ allDay: true, recurring: { repeat: 'daily' as const } }];
      }

      const dayBeforeFrom = pickerBounds.from.subtract(1, 'day');
      const dayAfterTo = pickerBounds.to.add(1, 'day');

      return [
        {
          allDay: true,
          start: '1900-01-01',
          end: dayBeforeFrom.format('YYYY-MM-DD'),
        },
        {
          allDay: true,
          start: dayAfterTo.format('YYYY-MM-DD'),
          end: '2100-12-31',
        },
      ];
    }, [pickerBounds, unrestricted]);

    const pickerRefDate = useMemo(() => {
      if (unrestricted || !('from' in pickerBounds)) {
        return undefined;
      }

      return pickerBounds.from.startOf('month').toDate();
    }, [pickerBounds, unrestricted]);

    const pickerSx = useMemo(
      () => ({
        ...getActivityDateRangePickerSx(theme),
        ...(sx ?? {}),
      }),
      [sx, theme]
    );

    const handleDateChange = useCallback(
      (_event: unknown, inst: { value?: unknown }) => {
        const normalizedRange = normalizeMobiscrollRangeValue(inst.value);
        if (!normalizedRange) {
          return;
        }

        if (
          !unrestricted &&
          'from' in pickerBounds &&
          !isMobiscrollRangeWithinBounds(normalizedRange, pickerBounds)
        ) {
          return;
        }

        onChange?.(normalizedRange);
      },
      [onChange, pickerBounds, unrestricted]
    );

    const pickerKey = unrestricted
      ? `${instanceKey}-unrestricted`
      : `${instanceKey}-${'from' in pickerBounds ? pickerBounds.from.format('YYYY-MM-DD') : 'none'}-${'to' in pickerBounds ? pickerBounds.to.format('YYYY-MM-DD') : 'none'}`;

    return (
      <Box sx={pickerSx}>
        <Datepicker
          key={pickerKey}
          select="range"
          display="inline"
          pages={pages}
          theme="material"
          themeVariant={theme.palette.mode === 'dark' ? 'dark' : 'light'}
          touchUi={false}
          buttons={[]}
          showRangeLabels={false}
          {...(pickerRefDate ? { refDate: pickerRefDate } : {})}
          {...(pickerInvalidRanges ? { invalid: pickerInvalidRanges } : {})}
          value={value ?? undefined}
          onChange={handleDateChange}
        />
      </Box>
    );
  }
);

ActivityDateRangePicker.displayName = 'ActivityDateRangePicker';
